import React from 'react';
import Modal from 'react-modal';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import {
	Contract,
	ContractsService,
} from '../../../services/contracts.service';
import DashedDivider from '../../DashedDivider/DashedDivider';
import { Person } from '../../../assets/Person';
import ProducerCompanyIcon from '../../../assets/ProducerCompanyIcon';
import Flag from 'react-world-flags';
import { Calendar } from '../../../assets/Calendar';
import dayjs from 'dayjs';
import { AdminContractsRed } from '../../../assets/Contracts/AdminContractsRed';
import { toastVariant } from '../../ToastVariant/toastVariant';

type ModalCancelContractProps = {
	contract: Contract;
	isOpen: boolean;
	onClose: () => void;
	onUpdated?: (updated: Contract) => void;
};

const ModalCancelContract: React.FC<ModalCancelContractProps> = ({
	contract,
	isOpen,
	onClose,
	onUpdated,
}) => {
	const { t } = useTranslate();

	const handleConfirm = async () => {
		try {
			const { data } = await ContractsService.editContract(contract.id, {
				status: 'canceled',
			});

			toastVariant(
				t('adminContracts.contractCanceled', 'Contract canceled successfully'),
				false,
			);
			onUpdated?.(data);
			onClose();
		} catch (error) {}
	};

	return (
		<Modal
			isOpen={!!isOpen}
			onRequestClose={onClose}
			className="modal-inside outline-none max-w-[540px]"
			overlayClassName="modal-overlay"
			shouldCloseOnOverlayClick={false}
			ariaHideApp={false}
		>
			<div className="w-[532px] h-fit flex flex-col box-sizing">
				<div className="m-3">
					<AdminContractsRed />
				</div>

				<div className="flex flex-col gap-3 mx-3 mb-3">
					<SypacText variant="body-regular-medium">
						<p className="text-[20px] leading-[32px] text-gray-80">
							<T keyName="modalCancelContract.areYouSure">
								Are you sure you want to cancel the contract?
							</T>
						</p>
					</SypacText>

					<div className="flex flex-col gap-3 p-3 bg-alabaster border border-dashed border-gray-10 rounded-10">
						<SypacText variant="body-regular-medium">
							<p className="text-[14px] leading-[16px] text-gray-40">
								<T keyName="adminContracts.contractDetails">
									Contract details:
								</T>
							</p>
						</SypacText>
						<DashedDivider dashLength={5} gapLength={5} />

						<div className="h-fit min-h-[16px] grid grid-cols-[126px,auto] gap-5 items-start">
							<div className="h-[16px] flex items-center gap-3">
								<Person color="#A2A2A2" />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[14px] leading-[16px] mt-[2px]">
										<T keyName="adminContracts.buyer">Buyer</T>
									</p>
								</SypacText>
							</div>

							<div className="flex flex-col gap-3 items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
										{contract.buyer.name}
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-50 text-[14px] leading-[16px]">
										{`${contract.buyerRepresentative?.firstName} ${contract.buyerRepresentative?.lastName}`}
									</p>
								</SypacText>

								<div className="flex items-start gap-2">
									{contract.buyer.countryCode ? (
										<Flag
											className="object-cover border border-solid border-gray-10 rounded"
											code={contract.buyer.countryCode}
											width={19}
											height={14}
										/>
									) : null}
									<SypacText variant="body-regular-medium">
										<p className="text-[12px] leading-[14px] text-gray-40 whitespace-normal break-words">
											{contract.buyer.registryAddress}
										</p>
									</SypacText>
								</div>
							</div>
						</div>
						<DashedDivider dashLength={5} gapLength={5} />

						<div className="h-fit min-h-[16px] grid grid-cols-[126px,auto] gap-5 items-start">
							<div className="h-[16px] flex items-center gap-3">
								<ProducerCompanyIcon color="#A2A2A2" />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[14px] leading-[16px] mt-[2px]">
										<T keyName="adminContracts.seller">Seller</T>
									</p>
								</SypacText>
							</div>

							<div className="flex flex-col gap-3 items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
										{contract.seller.name}
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-50 text-[14px] leading-[16px]">
										{`${contract.sellerRepresentative?.firstName} ${contract.sellerRepresentative?.lastName}`}
									</p>
								</SypacText>

								{contract.lines?.[0].product.productBase ? (
									<div className="flex items-start gap-3">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-50 text-[14px] leading-[16px] whitespace-nowrap">
												{contract.lines?.[0].product.productBase.name}
											</p>
										</SypacText>
										<div className="w-[2px] h-[2px] bg-gray-40 rounded-full mt-[6px]" />
										<div className="flex items-start gap-2">
											{contract.lines?.[0].product.productBase.countryCode ? (
												<span>
													<Flag
														className="object-cover border border-solid border-gray-10 rounded"
														code={
															contract.lines?.[0].product.productBase
																.countryCode
														}
														width={20}
														height={14}
													/>
												</span>
											) : null}
											<SypacText variant="body-regular-medium">
												<p className="text-gray-50 text-[14px] leading-[16px]">
													{contract.lines?.[0].product.productBase.address}
												</p>
											</SypacText>
										</div>
									</div>
								) : null}
							</div>
						</div>
						<DashedDivider dashLength={5} gapLength={5} />

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.productName">Product name:</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80 text-[14px] leading-[16px]">
									{contract.lines?.[0].product.name}
								</p>
							</SypacText>
						</div>

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.contractQuantity">
										Contract quantity:
									</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80 text-[14px] leading-[16px]">
									{contract.lines?.[0].contractQtyTons}{' '}
									<T keyName="basicDetails.tons">tons</T>
								</p>
							</SypacText>
						</div>
						<DashedDivider dashLength={5} gapLength={5} />

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.contractDateFrom">
										Contract date from:
									</T>
								</p>
							</SypacText>

							<div className="h-[16px] flex items-center gap-3">
								<Calendar />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
										{dayjs(contract.startsAt).format('DD.MM.YYYY')}
									</p>
								</SypacText>
							</div>
						</div>

						<div className="h-[16px] grid grid-cols-[126px,auto] gap-5 items-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.contractDateTo">
										Contract date to:
									</T>
								</p>
							</SypacText>

							<div className="h-[16px] flex items-center gap-3">
								<Calendar />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
										{dayjs(contract.expiresAt).format('DD.MM.YYYY')}
									</p>
								</SypacText>
							</div>
						</div>
					</div>
				</div>

				<div className="flex gap-3 border-0 border-t border-solid border-gray-10 p-3">
					<SypacButton variant="secondary" size="small" className="w-full">
						<button
							type="button"
							className="w-full h-[42px] flex items-center justify-center rounded-lg transition"
							onClick={onClose}
						>
							<SypacText variant="body-regular-medium">
								<p className="text-gray-80">
									<T keyName="userDetails.back">Back</T>
								</p>
							</SypacText>
						</button>
					</SypacButton>
					<SypacButton variant="secondary" size="small" className="w-full">
						<button
							type="button"
							className="w-full h-[42px] flex items-center justify-center bg-red border-0 rounded-lg transition hover:bg-red/70"
							onClick={handleConfirm}
						>
							<SypacText variant="body-regular-medium">
								<p className="text-white">
									<T keyName="modalSelfPickup.confirm">Confirm</T>
								</p>
							</SypacText>
						</button>
					</SypacButton>
				</div>
			</div>
		</Modal>
	);
};

export default ModalCancelContract;
